import styled from '@emotion/styled';
import { Formik } from 'formik';
import { keyBy, mapValues } from 'lodash';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import CMSService from 'App/services/CMSService';
import convertToastUISyntax from 'App/utils/convertToastUISyntax/convertToastUISyntax';
import { queryClient } from 'Shared/queryClient';
import toast from 'Shared/toast';
import contentGroupPropType from '../types/contentGroupPropType';
import ContentLibraryEditFormArea from './components/ContentLibraryEditFormArea';
import ContentLibraryScrollspy from './ContentLibraryScrollspy';

const convertContentVersionToView = (contentVersion) => ({
    ...contentVersion,
    content: convertToastUISyntax(contentVersion.content, true),
});

const ContentLibraryContainerStyle = styled.form`
    display: flex;
    flex: 1 1 auto;
    overflow: auto;
`;

const ContentLibraryEditForm = ({ contentGroup }) => {
    const { groupUuid } = useParams();

    const initialValues = useMemo(() => {
        return {
            contentVersions: keyBy(contentGroup.contentVersions.map(convertContentVersionToView), 'uuid'),
            children: mapValues(contentGroup.children, (child) => ({
                ...child,
                contentVersions: keyBy(child.contentVersions.map(convertContentVersionToView), 'uuid'),
            })),
        };
    }, [contentGroup.children, contentGroup.contentVersions]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                const topLevelSavePromises = Object.values(values.contentVersions)
                    .filter((contentVersion) => {
                        return initialValues.contentVersions[contentVersion.uuid].content !== contentVersion.content;
                    })
                    .map((contentVersion) => {
                        return CMSService.saveContentVersion(contentVersion.uuid, {
                            content: contentVersion.content,
                        });
                    });

                const childSavePromises = Object.values(values.children).flatMap((child) => {
                    return Object.values(child.contentVersions)
                        .filter((contentVersion) => {
                            const initialChild = initialValues.children[child.uuid];
                            return initialChild.contentVersions[contentVersion.uuid].content !== contentVersion.content;
                        })
                        .map((contentVersion) => {
                            return CMSService.saveContentVersion(contentVersion.uuid, {
                                content: contentVersion.content,
                            });
                        });
                });

                const deletePromises = Object.values(values.children)
                    .filter((child) => child.deleted)
                    .map((child) => CMSService.deleteContentGroup(child.uuid));

                const savePromises = [...topLevelSavePromises, ...childSavePromises, ...deletePromises];
                await Promise.all(savePromises);
                toast.success('Monograph draft saved successfully.');
                queryClient.invalidateQueries(['contentGroup', groupUuid]);
            }}
        >
            {({ handleSubmit }) => (
                <ContentLibraryContainerStyle onSubmit={handleSubmit}>
                    <ContentLibraryEditFormArea contentGroup={contentGroup} />

                    <ContentLibraryScrollspy contentGroup={contentGroup} />
                </ContentLibraryContainerStyle>
            )}
        </Formik>
    );
};

ContentLibraryEditForm.propTypes = {
    contentGroup: contentGroupPropType.isRequired,
};

export default ContentLibraryEditForm;
